/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable no-unused-vars */
import Navigation from "@/components/Navigation/Navigation";
import LabelCartItem, {
  LabelCartItemAdd,
  LabelCartItemChild,
  LabelNotes,
} from "@/components/ui/Label/LabelCartItem";
import CircleLoader from "@/components/ui/Loader/CircleLoader";
import UserHeader from "@/components/User/UserHeader";
import nf from "@/constants/nf";
import usePatch from "@/hooks/request/usePatch";
import { getCustomer } from "@/store/slices/authSlice";
import {
  generateDiscountValue,
  getEverything,
  getSubTotal,
} from "@/store/slices/cartSlice";

import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import QuantityControl from "../Item/QuantityControl";
import { route } from "@/constants/routeName";

import shopping from "@/assets/img/shopping.png";
import Notes from "@/components/Modal/Notes";
import { EditNote } from "@mui/icons-material";
import { getServingTimes } from "@/store/slices/contentSlice";
import { getCutOffTime } from "@/helper/getCutOffTime";
import getRemainingTime from "@/helper/getRemainingTime";
import { canOrderForChild } from "@/helper/dashboardStatus";
import { wTextST } from "@/constants/wTextST";
import WarningServingTime from "@/components/Modal/WarningServingTime";

export default function Cart() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const innerHeight = useSelector((state) => state.content.innerHeight);
  const cartItems = useSelector((state) => state.cart.items);
  const cart = useSelector((state) => state.cart);
  const selectedOutlet = useSelector((state) => state.auth.selectedOutlet);
  const children = useSelector((state) => state.auth.children) || [];
  const payType = useSelector((state) => state.content.payType);
  const selectedCustomer = useSelector((state) => state.auth.selectedCustomer);
  const accountType = useSelector((state) => state.auth.accountType);
  const cartCustomerId = useSelector((state) => state.cart.customerId);
  const discountCustomer = useSelector((state) => {
    const disc = state.cart.discount;
    const forId = state.cart.discountForCustomerId;
    const selectedId = state.auth.selectedCustomer?.id;
    if (disc && forId != null && selectedId != null && Number(forId) === Number(selectedId)) {
      return disc;
    }
    if (disc && forId == null) return disc;
    return null;
  });

  const utcOffset = useSelector((state) => state.content.utcOffset);
  const outlet = useSelector((state) => state.content.outlet);

  const [openNotes, setOpenNotes] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const checkoutCustomerRef = useRef(null);

  const totalDepositAfterOrder =
    cartItems?.length > 0
      ? (+selectedCustomer.balance || 0) - (cart.total + cart.totalAddOns)
      : 0;
  const totalOrderHitThreshold =
    cartItems?.length > 0 && Math.sign(totalDepositAfterOrder) === -1;
  const disableOrder =
    outlet?.use_minimum_balance_limit &&
    totalDepositAfterOrder <= +outlet.minimum_balance_limit;

  const {
    mutate: saveOrders,
    isPending,
  } = usePatch({
    url: `point-of-sale/orders/process_bulk`,
    method: "post",
    callback: () => {
      const paidForId = checkoutCustomerRef.current ?? selectedCustomer?.id;
      if (paidForId != null) {
        dispatch(getCustomer({ params: {}, id: paidForId }));
      }
      toast.success("Order has been paid successfully");
      // Stay on menu so parents can order again; dashboard is `/` (PA-05).
      navigate(`/${route.menu}`);
      dispatch(getServingTimes({ outlet: selectedOutlet?.id, max_size: true }));
      setTimeout(() => {
        dispatch({ type: "cart/resetItems" });
      }, 300);
    },
    onError: () => {
      toast.error("There is an Error with order");
    },
    showError: true,
  });

  useEffect(() => {
    dispatch({ type: "auth/setSwitchingLocked", payload: isPending });
    return () => {
      dispatch({ type: "auth/setSwitchingLocked", payload: false });
    };
  }, [isPending, dispatch]);

  const items = useMemo(() => {
    return cartItems.length
      ? cartItems.reduce((group, product) => {
          const { date } = product;
          const dateKey = dayjs(date).format("YYYY-MM-DD");
          group[dateKey] = group[dateKey] ?? [];
          group[dateKey].push(product);
          return group;
        }, {})
      : [];
  }, [cartItems]);

  // Define getSTime function before using it in useMemo
  const getSTime = (s, orderDate) => {
    const { oCutOff, sT } = getCutOffTime(s, orderDate, utcOffset);
    const remainingDayTime = getRemainingTime(oCutOff);
    // Fix: isDisable should be true when time has expired (remainingDayTime < 0)
    let isDisable = remainingDayTime < 0;

    return { isDisable, oCutOff, sT };
  };

  // Check if any items have exceeded cutoff time
  const hasExpiredItems = useMemo(() => {
    return cartItems.some((item) => {
      if (!item.serving_time) return false;
      const { isDisable } = getSTime(item.serving_time, dayjs(item.date));
      return isDisable;
    });
  }, [cartItems]);

  // Disable order if there are expired items or insufficient balance
  const cannotPlaceOrder = disableOrder || hasExpiredItems;

  const changeAmount = (type, item) => {
    dispatch({ type: "cart/setItemQuantity", payload: { type, item } });
  };

  const saveTheOrder = async () => {
    if (disableOrder) return;

    // Capture at click time — do not re-read selector after a late switch
    const checkoutCustomerId = cartCustomerId ?? selectedCustomer?.id ?? null;
    if (checkoutCustomerId == null) {
      toast.error("No student selected for this order");
      return;
    }
    if (
      cartCustomerId != null &&
      selectedCustomer?.id != null &&
      Number(cartCustomerId) !== Number(selectedCustomer.id)
    ) {
      toast.error("Cart belongs to another student. Stay on that child or clear the cart.");
      return;
    }

    const checkoutChild =
      children.find((c) => Number(c.id) === Number(checkoutCustomerId)) ||
      selectedCustomer;
    if (!canOrderForChild(checkoutChild)) {
      toast.error("This student is inactive for your family. You cannot place an order.");
      return;
    }

    checkoutCustomerRef.current = checkoutCustomerId;
    const orderType = accountType === "parent" ? "Parent App" : "Customer App";

    const orderData = Object.keys(items).map((d) => {
      const pItems = items[d];
      const objOrder = {
        outlet: selectedOutlet.id,
        customer: checkoutCustomerId,
        note: items[d][0]?.notes || "",
        date: d,
        products: pItems.map((p) => {
          const objP = {
            product: p.id,
            quantity: p.quantity,
            is_void: p.is_void,
          };
          if (p.add_ons?.length) {
            objP.add_ons = p.add_ons.map((m) => {
              return m.add_on.id;
            });
          }
          if (p.modifiers?.length) {
            objP.modifiers = p.modifiers.map((m) => {
              return m.modifier.id;
            });
          }
          objP.serving_time = p.serving_time?.id || null;
          if (p.discount?.type) {
            objP.discount = +p.discount?.value || 0;
            objP.discount_type = p.discount?.type || null;
          }

          // Fix: Check if serving time exists and validate cutoff time
          if (p.serving_time) {
            const { isDisable } = getSTime(p.serving_time, dayjs(p.date));
            objP.isValid = !isDisable; // Fix: isValid = true when NOT disabled (time still available)
          } else {
            objP.isValid = false; // No serving time selected
          }

          objP.note = p.notes || null;

          return objP;
        }),
        payment_type: payType.deposit,
        type: orderType,
      };
      return objOrder;
    });

    // Fix: Check if any products have exceeded the order cutoff limit
    const checkIfOrderTimeIsExpired = orderData.some((d) =>
      d.products.some((p) => p.serving_time && !p.isValid)
    );

    if (checkIfOrderTimeIsExpired) {
      setShowWarning(true);
      return;
    }

    await saveOrders(orderData);
  };

  return (
    <div style={{ minHeight: innerHeight }} className="app-container">
      <div className="relative flex flex-col w-full h-full gap-4 p-4 pb-24">
        <UserHeader />
        {selectedCustomer?.member?.name && cartItems?.length > 0 && (
          <p className="text-sm text-[var(--weak)]" data-testid="cart-ordering-for">
            Ordering for{" "}
            <span className="font-bold text-black">{selectedCustomer.member.name}</span>
          </p>
        )}
        {/* Show expired items warning banner */}
        {hasExpiredItems && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
            <span className="text-red-600 text-lg">⚠️</span>
            <div className="flex flex-col">
              <span className="text-red-700 font-semibold">
                Order Cutoff Time Exceeded
              </span>
              <span className="text-red-600 text-sm">
                Some items in your cart have exceeded their ordering deadline.
                Please remove expired items or contact staff to proceed.
              </span>
            </div>
          </div>
        )}
        {cartItems?.length > 0 && (
          <div className="flex flex-col gap-2 mb-4">
            {Object.keys(items).map((k) => {
              const { valInlineDiscount, subT, valModifiers } = getEverything(
                items[k],
                discountCustomer
              );

              const total = subT + valModifiers;
              const totalDiscount = valInlineDiscount;
              const subTotal = total - totalDiscount;
              const notes = items[k][0]?.notes || "";
              const cItems = items[k].map((item) => {
                let discItem = item.discount
                  ? generateDiscountValue(item, false)
                  : 0;
                const showDiscItem = discItem.disc > 0;
                const { isDisable } = getSTime(
                  item.serving_time,
                  dayjs(item.date)
                );

                // Show warning if item has exceeded cutoff time
                const showExpiredWarning = item.serving_time && isDisable;

                return (
                  <div key={item.uid} className="flex flex-col gap-2">
                    <LabelCartItem
                      item={item}
                      value1={`${item.quantity}x`}
                      value2={item.name}
                      value3={item.selling_price}
                    />
                    {item.add_ons?.map((a) => {
                      return (
                        <LabelCartItemChild
                          key={a.id}
                          label={a.add_on.name}
                          value={
                            a.add_on.price ? `${nf(a.add_on.price)}` : "Free"
                          }
                        />
                      );
                    })}
                    {item.modifiers?.map((a) => {
                      return (
                        <LabelCartItemChild
                          key={a.id}
                          label={a.modifier.name}
                          value="Free"
                        />
                      );
                    })}
                    {!!discountCustomer && showDiscItem && (
                      <LabelCartItemAdd
                        label={`disc (${nf(discItem.disc)}) ${
                          discountCustomer?.name
                            ? `- ${discountCustomer.name}`
                            : ""
                        }`}
                        value=""
                      />
                    )}
                    <LabelCartItemAdd
                      label="Serving Time"
                      value={item.serving_time?.name}
                    />

                    <div className="carts items">
                      <span />
                      <div className="">
                        {item?.notes && <div>{`Notes : ${item?.notes}`}</div>}
                        <Link
                          onClick={() => {
                            dispatch({
                              type: "cart/setSelectedItem",
                              payload: item,
                            });
                          }}
                          to={`/${route.cartItem}/${item.uid}/`}
                          className="px-2 text-left border border-black rounded-lg w-fit"
                        >
                          Edit
                        </Link>
                      </div>
                      <QuantityControl
                        item={item}
                        amount={item.quantity}
                        changeAmount={changeAmount}
                        config={{ className: "justify-end" }}
                      />
                    </div>
                    <div className="carts items">
                      <span />
                    </div>
                    {/* Show expired warning with better styling */}
                    {showExpiredWarning && (
                      <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <span className="text-red-600 text-sm font-medium">
                          ⚠️ Order Cutoff Exceeded
                        </span>
                        <span className="text-red-500 text-sm">{wTextST}</span>
                      </div>
                    )}
                  </div>
                );
              });
              const date = dayjs(k).format("dddd, DD MMMM YYYY");
              return (
                <div key={k} className="flex flex-col gap-2">
                  <span className="font-bold">{date}</span>
                  <div className="flex flex-col gap-4">{cItems}</div>
                  <LabelNotes notes={notes}>
                    <button
                      onClick={() =>
                        setOpenNotes({ date: items[k][0].date, notes })
                      }
                      className="flex items-center justify-center gap-1 px-2 text-left border border-black rounded-lg w-fit"
                    >
                      Notes
                      <EditNote />
                    </button>
                  </LabelNotes>
                  {!!discountCustomer && totalDiscount > 0 && (
                    <>
                      <LabelCartItemAdd label="Total" value={nf(total || 0)} />
                      <LabelCartItemAdd
                        label="Discount"
                        value={`-${nf(totalDiscount || 0)}`}
                      />
                    </>
                  )}
                  <LabelCartItemAdd
                    label="Sub Total"
                    value={nf(subTotal || 0)}
                  />
                </div>
              );
            })}
          </div>
        )}
        {totalOrderHitThreshold && (
          <span>
            Sorry, your deposit is insufficient. After your recent order your
            remaining balance is{" "}
            <span className="text-red-500">
              {nf(
                (+selectedCustomer.balance || 0) -
                  (cart.total + cart.totalAddOns)
              )}
            </span>
            . Please contact our admin for your top up. Kind regards Jungle
            Toppings
          </span>
        )}
        {cartItems?.length > 0 && (
          <button
            className="btn-actions"
            onClick={saveTheOrder}
            disabled={cannotPlaceOrder || isPending}
          >
            {isPending && <CircleLoader className="me-2" />}
            Purchase{" "}
            <span className="ml-1">{nf(cart.total + cart.totalAddOns)}</span>
          </button>
        )}
        {cartItems?.length === 0 && (
          <div className="flex flex-col items-center justify-center m-auto">
            <div className="mx-auto text-center">
              <img
                src={shopping}
                className="w-auto h-36"
                alt="Empty icons created by Roundicons Premium - Flaticon"
              />
            </div>
            <span>Your cart is empty</span>
          </div>
        )}
      </div>
      <Navigation />
      {!!openNotes && (
        <Notes
          open={!!openNotes}
          onClose={() => setOpenNotes(false)}
          data={openNotes}
        />
      )}
      <WarningServingTime
        open={showWarning}
        onClose={() => setShowWarning(false)}
        from="cart"
        onClearCart={() => {
          setShowWarning(false);
          dispatch({ type: "cart/resetItems" });
        }}
      />
    </div>
  );
}
